/* eslint-disable */
// => https://gitlab.com/gitlab-org/gitlab-ui/-/blob/main/src/components/base/filtered_search/filtered_search.vue
// Copyright GitLab B.V.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import PortalVue from 'portal-vue';
import Vue from 'vue';
import {
    GlTooltipDirective, GlSearchBoxByClick, GlIcon,
} from '@gitlab/ui';
import {
  isEmptyTerm,
  INTENT_ACTIVATE_PREVIOUS,
  createTerm,
  ensureTokenId,
  normalizeTokens,
  denormalizeTokens,
  needDenormalization,
  termTokenDefinition,
} from './filtered_search_utils';
import { ApckGlFilteredSearchTerm } from './apck_glfiltered_search_term';
Vue.use(PortalVue);

let portalUuid = 0;

function initialState() {
  return [createTerm()];
}

export const ApckGlFilteredSearch = {
  name: 'GlFilteredSearch',
  components: {
    GlSearchBoxByClick,
    GlIcon,
  },
  directives: { GlTooltip: GlTooltipDirective },
  provide() {
    portalUuid += 1;
    this.portalName = `filters_portal_${portalUuid}`;

    return {
      portalName: this.portalName,
      alignSuggestions: (ref) => this.alignSuggestions(ref),
      // Return a function reference instead of a prop to work around vue-apollo@3 bug.
      // TODO: This can be reverted once https://github.com/vuejs/vue-apollo/pull/1153
      // has been merged and we consume it, or we upgrade to vue-apollo@4.
      suggestionsListClass: () => this.suggestionsListClass,
      termsAsTokens: () => this.termsAsTokens,
      updateSuggestionsPositionOnScroll: () => this.updateSuggestionsPositionOnScroll,
    };
  },
  inheritAttrs: false,
  props: {
    /**
     * If provided, used as value of filtered search
     */
    value: {
      required: false,
      type: Array,
      default: () => [],
    },
    /**
     * Available tokens
     */
    availableTokens: {
      type: Array,
      required: false,
      default: () => [],
      validator(value) {
        // eslint-disable-next-line no-underscore-dangle
        if (!value.__v_raw) {
          // This is Vue 2
          return true;
        }

        // eslint-disable-next-line no-underscore-dangle
        const isOk = Array.isArray(value) && value.every(({ token }) => token.__v_skip);
        if (!isOk) {
          // eslint-disable-next-line no-console
          console.warn(
            'You are using Vue3. In Vue3 each token component passed to filtered search must be wrapped into markRaw'
          );
        }

        return isOk;
      },
    },
    /**
     * If provided, used as history items for this component
     */
    placeholder: {
      type: String,
      required: false,
      default: 'Search',
    },
    clearButtonTitle: {
      type: String,
      required: false,
      default: 'Clear',
    },
    historyItems: {
      type: Array,
      required: false,
      default: null,
    },
    /**
     * Additional classes to add to the suggestion list menu. NOTE: this not reactive, and the value
     * must be available and fixed when the component is instantiated
     */
    suggestionsListClass: {
      type: [String, Array, Object],
      required: false,
      default: null,
    },
    /**
     * Display operators' descriptions instead of their values (e.g., "is" instead of "=").
     */
    showFriendlyText: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * HTML attributes to add to the search button
     */
    searchButtonAttributes: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    /**
     * HTML attributes to add to the search input
     */
    searchInputAttributes: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    viewOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Render search terms as GlTokens. Ideally, this prop will be as
     * short-lived as possible, and this behavior will become the default and
     * only behavior.
     *
     * This prop is *not* reactive.
     *
     * See https://gitlab.com/gitlab-org/gitlab-ui/-/issues/2159.
     */
    termsAsTokens: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The title of the text search option. Ignored unless termsAsTokens is enabled.
     */
    searchTextOptionLabel: {
      type: String,
      required: false,
      default: termTokenDefinition.title,
    },
    updateSuggestionsPositionOnScroll: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      tokens: initialState(),
      activeTokenIdx: null,
      suggestionsStyle: {},
      intendedCursorPosition: 'end',
    };
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  computed: {
    activeToken() {
      return this.tokens[this.activeTokenIdx];
    },
    lastTokenIdx() {
      return this.tokens.length - 1;
    },
    isLastTokenActive() {
      return this.activeTokenIdx === this.lastTokenIdx;
    },
    hasValue() {
      return this.tokens.length > 1 || this.tokens[0].value.data !== '';
    },
    termPlaceholder() {
      return this.hasValue ? null : this.placeholder;
    },
    currentAvailableTokens() {
      return this.availableTokens.filter((token) => {
        if (token.disabled) {
          return false;
        }

        if (token.unique) {
          return !this.tokens.find((t) => t.type === token.type);
        }

        return true;
      });
    },
  },
  watch: {
    tokens: {
      handler() {
        if (process.env.NODE_ENV !== 'production') {
          const invalidToken = this.tokens.find((token) => !token.id);
          if (invalidToken) {
            throw new Error(`Token does not have an id:\n${JSON.stringify(invalidToken)}`);
          }
        }

        if ((this.tokens.length === 0 || !this.isLastTokenEmpty()) && !this.viewOnly) {
          this.tokens.push(createTerm());
        }

        /**
         * Emitted when the tokens (value) changes
         * @property {array} tokens
         */
        this.$emit('input', this.tokens);
      },
      deep: true,
      immediate: true,
    },
    value: {
      handler(newValue, oldValue) {
        if (newValue.length && !isEqual(newValue, oldValue)) {
          this.applyNewValue(cloneDeep(newValue));
        }
      },
      deep: true,
      immediate: true,
    },
    activeTokenIdx: {
      handler(newValue) {
        this.emitUpdatedTokens();
      },
      immediate: true
    }
  },

  methods: {
    handleClickOutside(event) {
      const container = this.$el;
      const isClickInside = container.contains(event.target);
      if (!isClickInside) {
        this.closeDropdown();
      }
    },
    closeDropdown() {
      if (this.activeTokenIdx !== null){
        this.destroyToken(this.activeTokenIdx);
      }
    },
    applyNewValue(newValue) {
      this.tokens = needDenormalization(newValue)
        ? denormalizeTokens(newValue, this.termsAsTokens)
        : newValue;
    },

    isActiveToken(idx) {
      return this.activeTokenIdx === idx;
    },

    isLastToken(idx) {
      return this.activeTokenIdx === null && idx === this.lastTokenIdx;
    },

    isLastTokenEmpty() {
      return isEmptyTerm(this.tokens[this.lastTokenIdx]);
    },

    getTokenEntry(type) {
      return this.availableTokens.find((t) => t.type === type);
    },

    getTokenComponent(type) {
      return this.getTokenEntry(type)?.token || ApckGlFilteredSearchTerm;
    },

    getTokenClassList(idx) {
      return {
        'gl-filtered-search-item': true,
        'gl-filtered-search-last-item': this.isLastToken(idx) && !this.viewOnly,
      };
    },

    activate(idx) {
      if (!this.viewOnly) {
        this.activeTokenIdx = idx;
      }
    },

    activatePreviousToken() {
      if (this.activeTokenIdx > 0) {
        this.activeTokenIdx -= 1;
        this.intendedCursorPosition = 'end';
      }
    },

    activateNextToken() {
      // if (this.activeTokenIdx < this.value.length) {
      // Compare to this.tokens since it will always have the most up-to-date value.
      if (this.activeTokenIdx < (this.tokens.length - 1)) {
        this.activeTokenIdx += 1;
        this.intendedCursorPosition = 'start';
      } else {
        this.completeToken();
      }
    },

    alignSuggestions(ref) {
      const refRect = ref.getBoundingClientRect();
      const menuRect = this.$el.getBoundingClientRect();
      const translateX = Math.floor(refRect.left - menuRect.left);
      const translateY = Math.floor(menuRect.top - refRect.top);
      if (this.updateSuggestionsPositionOnScroll) {
        this.suggestionsStyle = `top: ${refRect.top + 40}px; left: ${refRect.left}px;`;
      } else {
        this.suggestionsStyle = `transform: translate(${translateX}px, ${translateY}px)`;
      }
    },

    deactivate(token) {
      this.intendedCursorPosition = 'end';
      const tokenIdx = this.tokens.indexOf(token);
      if (tokenIdx === -1 || this.activeTokenIdx !== tokenIdx) {
        return;
      }

      if (!this.isLastTokenEmpty()) {
        this.tokens.push(createTerm());
      }

      if (!this.isLastTokenActive && isEmptyTerm(this.activeToken)) {
        this.tokens.splice(tokenIdx, 1);
      }

      this.activeTokenIdx = null;
    },

    destroyToken(idx, { intent } = {}) {
      if (this.tokens.length === 1) {
        return;
      }

      this.tokens.splice(idx, 1);

      // First, attempt to honor the user's activation intent behind the
      // destruction of the token, if any. Otherwise, try to maintain the
      // active state for the token that was active at the time. If that's not
      // possible, make sure no token is active.
      if (intent === INTENT_ACTIVATE_PREVIOUS) {
        // If there is a previous token, activate it; else, activate the first token
        this.activeTokenIdx = Math.max(idx - 1, 0);
      } else if (idx < this.activeTokenIdx) {
        // Preserve the active token's active status (it shifted down one index)
        this.activeTokenIdx -= 1;
      } else if (idx === this.activeTokenIdx) {
        // User destroyed the active token; don't activate another one.
        this.activeTokenIdx = null;
      }
      // Do nothing if there was no active token, or if idx > this.activeTokenIdx,
      // to preserve the active state of the remaining tokens.
      this.emitUpdatedTokens();
    },

    replaceToken(idx, token) {
      this.$set(this.tokens, idx, ensureTokenId({ ...token, value: { data: '', ...token.value } }));
      this.activeTokenIdx = idx;
    },

    // This method can be deleted once termsAsTokens behavior is the default.
    createTokens(idx, newStrings = ['']) {
      if (!this.isLastTokenActive && newStrings.length === 1 && newStrings[0] === '') {
        this.activeTokenIdx = this.lastTokenIdx;
        return;
      }

      const newTokens = newStrings.map((data) => createTerm(data));

      this.tokens.splice(idx + 1, 0, ...newTokens);

      this.activeTokenIdx = idx + newStrings.length;
    },

    completeToken() {
      if (this.activeTokenIdx === this.lastTokenIdx - 1) {
        this.activeTokenIdx = this.lastTokenIdx;
      } else {
        this.activeTokenIdx = null;
      }
    },

    submit() {
      /**
       * Emitted when search is submitted
       * @property {array} tokens
       */
      this.$emit('submit', normalizeTokens(cloneDeep(this.tokens)));
    },
    displayError(errorMsg) {
        this.$emit("displayError", errorMsg);
    },
    emitUpdatedTokens() {
      const cmp = this;
        cmp.$emit('tokensUpdated', normalizeTokens(cloneDeep(this.tokens)));
    }
  },
  template: `
  <gl-search-box-by-click
    v-bind="$attrs"
    :value="tokens"
    :history-items="historyItems"
    :clearable="hasValue"
    :search-button-attributes="searchButtonAttributes"
    :clear-button-title="clearButtonTitle"
    :disabled="viewOnly"
    data-testid="filtered-search-input"
    ref="searchbox"
    @submit="submit"
    @input="applyNewValue"
    @history-item-selected="$emit('history-item-selected', $event); emitUpdatedTokens();"
    @clear="$emit('clear'); $nextTick(() => emitUpdatedTokens());"
    @clear-history="$emit('clear-history')"
  >
    <template #history-item="slotScope">
      <!-- @slot Slot to customize history item in history dropdown. Used only if using history items -->
      <slot name="history-item" v-bind="slotScope"></slot>
    </template>
    <template #input>
      <div
        class="gl-filtered-search-scrollable-container"
        :class="{ 'gl-bg-gray-10! gl-inset-border-1-gray-100!': viewOnly }"
      >
        <div class="gl-filtered-search-scrollable">
          <component
            :is="getTokenComponent(token.type)"
            v-for="(token, idx) in tokens"
            ref="tokens"
            :key="token.id + idx"
            v-model="token.value"
            :config="getTokenEntry(token.type)"
            :active="activeTokenIdx === idx"
            :cursor-position="intendedCursorPosition"
            :available-tokens="currentAvailableTokens"
            :current-value="tokens"
            :index="idx"
            :placeholder="termPlaceholder"
            :show-friendly-text="showFriendlyText"
            :search-input-attributes="searchInputAttributes"
            :view-only="viewOnly"
            :is-last-token="isLastToken(idx)"
            :class="getTokenClassList(idx)"
            :search-text-option-label="searchTextOptionLabel"
            @displayError="displayError"            
            @activate="activate(idx)"
            @deactivate="deactivate(token)"
            @destroy="destroyToken(idx, $event)"
            @replace="replaceToken(idx, $event)"
            @complete="completeToken"
            @submit="submit"
            @split="createTokens(idx, $event)"
            @previous="activatePreviousToken"
            @next="activateNextToken"
          />
        </div>
      </div>
      <portal-target
        ref="menu"
        :key="activeTokenIdx"
        :name="portalName"
        slim
        :style="suggestionsStyle"
      />
    </template>
    <!--
  Emitted when search is cleared
  @event clear
  -->
    <!--
  Emitted when item from history is selected
  @event history-item-selected
  @property {object} value History item
  -->
    <!--
  Emitted when clear history button is clicked
  @event clear-history
  -->
  </gl-search-box-by-click>

  `,
};
